import { Link } from "react-router-dom";
import { ENHANCEMENTS, PHASES, PRODUCT_LOOP, type EnhanceStatus } from "../lib/product-roadmap";
import { PageHeader, Surface, Tag } from "../components/primitives";
import { roleCan, useRole } from "../lib/roles";
import { AccessDenied } from "./NewsletterEditorPage";

const STATUS: Record<EnhanceStatus, { label: string; tone: "teal" | "accent" | "purple" | "muted" }> = {
  now: { label: "In product", tone: "teal" },
  partial: { label: "Started", tone: "accent" },
  next: { label: "Phase next", tone: "purple" },
  later: { label: "Later", tone: "muted" },
  skipped: { label: "Skipped", tone: "muted" },
};

export function RoadmapPage() {
  const { role } = useRole();
  // Internal planning notes, for admins only.
  if (!roleCan(role, "settings.write")) return <AccessDenied need="Admins" />;
  return (
    <div style={{ display: "grid", gap: "var(--ui-space-5)" }}>
      <PageHeader
        title="Product roadmap"
        meta="Evolve Segment → Newsletter → Campaign into a lifecycle loop for Altibbi content, not a Mailchimp clone."
      />

      <div className="nl-banner">
        <div className="nl-banner-eyebrow">Architecture</div>
        <h2 className="nl-banner-title">User → category segments → send → events</h2>
        <p className="nl-banner-copy" style={{ marginTop: 16 }}>
          news.altibbi.me is an app, so crawlers see almost no HTML. This page is the enhancement plan for Marketing:
          combine country and category segments, then learn from clicks. Per-person interest personalization is later.
        </p>
      </div>

      <Surface>
        <div className="nl-arch">
          {PRODUCT_LOOP.map((step, i) => (
            <span key={`${step}-${i}`} className="nl-arch-step">
              {i > 0 && <span className="nl-arch-arrow" aria-hidden>→</span>}
              <span>{step}</span>
            </span>
          ))}
        </div>
      </Surface>

      <div className="nl-phase-row">
        {PHASES.map((p) => (
          <Surface key={p.id} style={{ padding: "var(--ui-space-4)" }}>
            <div className="pages-eyebrow">Phase {p.id}</div>
            <div style={{ fontWeight: 500, color: "var(--c-t1)", marginTop: 6 }}>{p.title.replace(/^Phase \d+ — /, "")}</div>
            <p style={{ margin: "8px 0 0", color: "var(--c-t3)", fontSize: "var(--ui-text-control)", lineHeight: 1.45 }}>{p.blurb}</p>
          </Surface>
        ))}
      </div>

      {PHASES.map((phase) => {
        const items = ENHANCEMENTS.filter((e) => e.phase === phase.id);
        return (
          <section key={phase.id} style={{ display: "grid", gap: "var(--ui-space-3)" }}>
            <div>
              <div className="pages-eyebrow">{phase.title}</div>
              <p style={{ margin: "6px 0 0", color: "var(--c-t3)", fontSize: "var(--ui-text-control)" }}>{phase.blurb}</p>
            </div>
            <div className="nl-enhance-grid">
              {items.map((item) => {
                const st = STATUS[item.status];
                const body = (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                      <h3 className="nl-enhance-title">{item.title}</h3>
                      <Tag tone={st.tone}>{st.label}</Tag>
                    </div>
                    <p className="nl-enhance-summary">{item.summary}</p>
                    <ul className="nl-enhance-list">
                      {item.points.map((pt) => <li key={pt}>{pt}</li>)}
                    </ul>
                  </>
                );
                return item.href ? (
                  <Link key={item.id} to={item.href} className="nl-enhance-card">{body}</Link>
                ) : (
                  <div key={item.id} className="nl-enhance-card">{body}</div>
                );
              })}
            </div>
          </section>
        );
      })}

      <Surface style={{ padding: "var(--ui-space-5)" }}>
        <div className="pages-eyebrow">Altibbi ecosystem</div>
        <h2 className="ui-display" style={{ margin: "8px 0 0", fontSize: 22, color: "var(--c-t1)" }}>
          Lifecycle layer, not only email
        </h2>
        <p style={{ margin: "12px 0 0", color: "var(--c-t2)", fontSize: "var(--ui-text-body)", lineHeight: 1.55, maxWidth: "68ch" }}>
          The main platform already has content, questions, doctors, medications, and telehealth.
          Sina adds Arabic health questions, labs, and follow-up. Newsletters should close the loop:
          behavior is captured, category segments pick who receives the send, and clicks inform the next issue.
        </p>
      </Surface>
    </div>
  );
}
